/**
 * PRD.330 — Reports → Clinic Collection Details.
 *
 * Thin assembly: Phase 1 definition + query builders (via module-level
 * bindings), Phase 2 controller / executor / shell. One row = one
 * `invoice_payments` payment event, dated by Payment Date (`paid_at`), for
 * this organization's locally synchronized data. This page never filters,
 * sorts, slices or totals rows — the query layer does.
 */
import { useMemo, type ReactNode } from 'react';
import {
  phpFromCents,
  type ClinicCollectionsColumnId,
  type ReportVisualizationResult,
} from '@helios/domain';
import type { ClinicCollectionRow } from '@helios/data-access';
import {
  ReportCategoryBars,
  ReportShell,
  ReportTimeSeriesBars,
  useReportController,
  type ReportColumnRenderers,
} from '../../components/reports';
import { useReportQuery } from '../../hooks/useReportQuery';
import { categoryFilterBinding } from '../../components/reports/category-filter-logic';
import { timeDrillBinding } from '../../components/reports/time-drill-logic';
import { useFormatMoney } from '../../hooks/useFormatMoney';
import { paymentMethodColor } from '../../lib/payment-method-colors';
import {
  COLLECTIONS_BY_METHOD,
  COLLECTIONS_OVER_TIME,
  CLINIC_COLLECTIONS_BINDINGS,
  CLINIC_COLLECTIONS_DEFINITION,
  CLINIC_COLLECTIONS_TEXT,
  clinicCollectionCellText,
  clinicCollectionsFacetLabel,
  emptyMessage,
} from './clinic-collection-details-config';

const rowKey = (row: ClinicCollectionRow) => row.payment_id;

const timeSeriesResult = (r: ReportVisualizationResult | undefined) =>
  r?.kind === 'timeSeries' ? r : undefined;
const categoryResult = (r: ReportVisualizationResult | undefined) =>
  r?.kind === 'categoryBar' ? r : undefined;

const muted = (text: string): ReactNode =>
  text === '—' ? <span style={{ color: 'var(--text-muted)' }}>—</span> : text;

export function ClinicCollectionDetailsPage() {
  const controller = useReportController(CLINIC_COLLECTIONS_DEFINITION);
  const result = useReportQuery<ClinicCollectionRow, ClinicCollectionsColumnId>(
    CLINIC_COLLECTIONS_DEFINITION,
    controller.query,
    CLINIC_COLLECTIONS_BINDINGS,
    clinicCollectionsFacetLabel,
  );
  const fmtMoney = useFormatMoney();

  const render = useMemo<ReportColumnRenderers<ClinicCollectionRow, ClinicCollectionsColumnId>>(
    () => ({
      paymentDate: (row) => clinicCollectionCellText(row, 'paymentDate'),
      patient: (row) => muted(clinicCollectionCellText(row, 'patient')),
      invoiceNo: (row) => muted(clinicCollectionCellText(row, 'invoiceNo')),
      method: (row) => (
        <span
          style={{
            color: paymentMethodColor(row.method)?.text,
            fontWeight: 'var(--font-weight-medium)',
          }}
        >
          {clinicCollectionCellText(row, 'method')}
        </span>
      ),
      amount: (row) => fmtMoney(phpFromCents(row.amount_cents)),
      notes: (row) => (
        <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {muted(clinicCollectionCellText(row, 'notes'))}
        </span>
      ),
    }),
    [fmtMoney],
  );

  const { from, to } = controller.query.dateRange;

  return (
    <ReportShell
      controller={controller}
      result={result}
      title={CLINIC_COLLECTIONS_TEXT.title}
      subtitle={CLINIC_COLLECTIONS_TEXT.subtitle}
      render={render}
      rowKey={rowKey}
      searchPlaceholder={CLINIC_COLLECTIONS_TEXT.searchPlaceholder}
      countLabel={CLINIC_COLLECTIONS_TEXT.countLabel}
      aggregateLabels={CLINIC_COLLECTIONS_TEXT.aggregateLabels}
      formatAggregate={(_key, cents) => fmtMoney(phpFromCents(cents))}
      facetLabel={clinicCollectionsFacetLabel}
      summaryNote={CLINIC_COLLECTIONS_TEXT.summaryNote}
      visualizations={
        // Each visualization comes from its own grouped query
        // (result.visualizations), never from the table's page rows.
        <>
          {/* PRD.330 Phase 5 — N/A (compact, full-width) for one-day ranges. */}
          <ReportTimeSeriesBars
            visualization={COLLECTIONS_OVER_TIME}
            result={timeSeriesResult(result.visualizations?.[COLLECTIONS_OVER_TIME.id])}
            formatPrimary={(cents) => fmtMoney(phpFromCents(cents))}
            formatSecondary={(n) => `${n} ${n === 1 ? 'payment' : 'payments'}`}
            emptyMessage={CLINIC_COLLECTIONS_TEXT.visualization.emptyMessage}
            // Period click drills the canonical date range into that period (Back / Esc return).
            {...timeDrillBinding(controller)}
            onRetry={result.retry}
          />
          <ReportCategoryBars
            visualization={COLLECTIONS_BY_METHOD}
            result={categoryResult(result.visualizations?.[COLLECTIONS_BY_METHOD.id])}
            columnLabels={CLINIC_COLLECTIONS_TEXT.visualization.columnLabels}
            emptyMessage={CLINIC_COLLECTIONS_TEXT.visualization.emptyMessage}
            formatPrimary={(cents) => fmtMoney(phpFromCents(cents))}
            colorFor={paymentMethodColor}
            // Chart click ⇄ the canonical Payment Method filter (no chart-only state).
            {...categoryFilterBinding(controller, COLLECTIONS_BY_METHOD.dimension)}
            onRetry={result.retry}
          />
        </>
      }
      messages={{ ...CLINIC_COLLECTIONS_TEXT.messages, empty: emptyMessage(from, to) }}
    />
  );
}

export default ClinicCollectionDetailsPage;
